'use client';

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { CartLevelGiftInput } from '../../../../types/front.graphql';
import { observer } from '@legendapp/state/react';
import type { AvailableCartLevelGiftsProps } from './available-cart-level-gifts.types';
import { SelectedGiftCart } from './components/selected-gift-cart';
import { SelectionGiftPrompt } from './components/selection-gift-prompt';
import { CartGiftsSelectorWidget, type CartGiftsSelectorWidgetRef } from '../cart-gifts-selector-widget';
import type { GiftConfirmParams } from '../cart-gifts-selector-widget/cart-gifts-selector-widget.types';
import { Fragment, Icon } from '@kidslabs/mario-nextjs-react-ui-core';
import { useCart, cartItemReferenceSynchronizationStore$, useCartItemReference } from '@kidslabs/module-quote';
import { useCartGiftSelection } from '../../hooks/use-cart-gift-selection.hook';
import type { SelectedGift } from '../../types/gift-selection.types';
import { useAvailableCartLevelGifts } from './use-available-cart-level-gifts-view.hook';

import styles from './available-cart-level-gifts.module.css';

/**
 * AvailableCartLevelGifts - Component to display cart-level gift options
 * Matches mobile implementation using ref pattern for CartGiftsSelectorWidget
 */
export const AvailableCartLevelGifts: React.FC<AvailableCartLevelGiftsProps> = observer(
    ({ showSelectedOnly = false, variant = 'compact', onCartPromptPress, isMobile, showActionButton }) => {
        // Get cartId from cart context
        const { cartId } = useCart();

        // Ref for cart gifts selector widget (matches mobile pattern)
        const cartGiftsSelectorRef = useRef<CartGiftsSelectorWidgetRef>(null);

        const {
            title,
            iconProps,
            cartLevelGiftRules,
            selectedGiftsCount,
            addCartLevelGiftsToCartMutation,
            addGiftsToCartError,
            refetchAvailableCartLevelGifts,
            removeCartLevelGiftFromCartMutation,
            giftsListLoading,
            hasGiftsContent,
        } = useAvailableCartLevelGifts({
            showSelectedOnly: showSelectedOnly,
        });

        const { removeAllSelectedGift } = useCartGiftSelection({ createNewSession: false });
        const { hasSelectedLogics, removeSelectedLogics } = useCartItemReference();

        // Refetch available gifts after cart sync completes (product changes).
        // syncVersion increments every time debounced cart sync succeeds,
        // so eligible gift rules are re-evaluated against the new cart state.
        const syncVersion = cartItemReferenceSynchronizationStore$.syncVersion.get();

        // Access cart gift selection store so SelectedGiftCart stays in sync
        // with server state after refetch (e.g. after cart sync).
        const cartGiftSelection = useCartGiftSelection({ createNewSession: false });

        // Sync server selected_gifts into Legend-State store whenever the
        // Apollo query data updates. This keeps SelectedGiftCart accurate
        // without requiring the user to re-open the bottom sheet.
        useEffect(() => {
            if (!cartLevelGiftRules.length) {
                // No eligible rules → clear the store
                const currentGifts = cartGiftSelection.store$.selectedGifts.peek() ?? [];
                if (currentGifts.length > 0) {
                    cartGiftSelection.store$.selectedGifts.set([]);
                }
                return;
            }

            const sessionId = cartGiftSelection.store$.sessionId.peek() ?? '';
            const giftsFromServer: SelectedGift[] = cartLevelGiftRules.flatMap(rule =>
                (rule.selected_gifts ?? [])
                    .filter((gift): gift is NonNullable<typeof gift> => gift !== null)
                    .map(gift => {
                        const giftProduct = rule.available_gifts
                            .filter((p): p is NonNullable<typeof p> => p !== null)
                            .find(p => p?.product?.sku === gift.sku);
                        const product = giftProduct?.product;
                        return {
                            id: `${rule.rule_id}-${gift.sku}-1`,
                            ruleId: rule.rule_id,
                            productId: product?.id ?? 0,
                            selectionRound: 1,
                            sessionId,
                            item: { ...product, maxQuantity: giftProduct?.max_qty },
                            giftData: {
                                __typename: 'FreeGiftsData' as const,
                                products: product ? [product] : [],
                                rule: {
                                    __typename: 'Rule' as const,
                                    rule_id: rule.rule_id,
                                    name: rule.rule_name ?? rule.rule_label ?? '',
                                },
                            },
                            quantity: gift.quantity,
                            selectedAt: Date.now(),
                            giftSlots: gift.quantity,
                        } as unknown as SelectedGift;
                    })
            );

            cartGiftSelection.store$.selectedGifts.set(giftsFromServer.slice(0, 1));
        }, [cartLevelGiftRules, cartGiftSelection.store$]);

        const renderIcon = useMemo(() => {
            return (
                <div className={styles.giftBoxContainer}>
                    <Icon type="data-uri" dataUriProps={iconProps} size={28} />
                </div>
            );
        }, [iconProps]);

        const handleGiftPromptPress = useCallback(() => {
            cartGiftsSelectorRef.current?.openBottomSheet();
            onCartPromptPress?.();
        }, [onCartPromptPress]);

        // Open bottom sheet filtered to a single rule (from "Đổi quà" button)
        const handleChangeGift = useCallback((ruleId: number) => {
            cartGiftsSelectorRef.current?.openBottomSheet(ruleId);
        }, []);

        const handleGiftsConfirm = useCallback(
            async ({ data, onSuccess, onError }: GiftConfirmParams) => {
                try {
                    // Group selected gifts by rule_id for the new API format
                    const giftsByRule = data.reduce(
                        (acc, gift) => {
                            const ruleId = gift.ruleId ?? 0;
                            if (!acc[ruleId]) {
                                acc[ruleId] = [];
                            }
                            acc[ruleId].push({
                                sku: gift.item.sku ?? '',
                                quantity: gift.quantity ?? 1,
                            });
                            return acc;
                        },
                        {} as Record<number, CartLevelGiftInput[]>
                    );

                    // Now, only support 1 rule
                    const currentSelectedRule = Object.entries(giftsByRule).at(0);

                    if (!currentSelectedRule) {
                        onError?.('Không có chương trình khuyến mãi nào được chọn');
                        return;
                    }

                    const [ruleIdStr, gifts] = currentSelectedRule;

                    const response = await addCartLevelGiftsToCartMutation({
                        variables: {
                            input: {
                                cart_id: cartId ?? '',
                                rule_id: Number(ruleIdStr),
                                gifts: gifts,
                            },
                        },
                    });

                    // Handle add cart level gift failed
                    if ((response?.data?.addCartLevelGiftsToCart?.errors?.length ?? 0) > 0) {
                        const error = response?.data?.addCartLevelGiftsToCart?.errors?.at(0) ?? {
                            message: 'Quà được chọn đã hết. Ba mẹ vui lòng chọn quà khuyến mãi khác',
                        };
                        onError?.(error?.message);

                        //Remove error local gift from store
                        removeAllSelectedGift();
                        refetchAvailableCartLevelGifts();
                        return;
                    } else {
                        // Remove selected cart items logic if  add cart gift success
                        removeSelectedLogics();

                        // Refetch available gifts data after successful mutation
                        refetchAvailableCartLevelGifts();

                        // Notify widget: success → it will close bottom sheet
                        onSuccess();
                    }
                } catch (error) {
                    console.error('Error in handleGiftsConfirm:', error);

                    // Still refetch to sync with server state
                    refetchAvailableCartLevelGifts();

                    // Notify widget: error → it keeps bottom sheet open
                    onError(addGiftsToCartError?.message || 'Failed to add gifts to cart.');
                }
            },
            [
                removeSelectedLogics,
                addGiftsToCartError?.message,
                addCartLevelGiftsToCartMutation,
                cartId,
                refetchAvailableCartLevelGifts,
                removeAllSelectedGift,
            ]
        );

        const handleGiftsClose = useCallback(() => {
            // Refetch available gifts when bottom sheet is dismissed
            refetchAvailableCartLevelGifts();
        }, [refetchAvailableCartLevelGifts]);

        const onBeforeGiftsConfirm = useCallback(
            async ({ data, onSuccess, onError }: GiftConfirmParams) => {
                if (!hasSelectedLogics()) {
                    handleGiftsConfirm({ data, onSuccess, onError });
                    return;
                }

                onError(
                    'Sản phẩm ba mẹ đang chọn đã có quà tặng. Nếu tiếp tục, quà tặng đã chọn trước đó sẽ bị thay thế',
                    () => handleGiftsConfirm({ data, onSuccess, onError }),
                    () => {
                        handleGiftsClose();
                    }
                );
            },
            [handleGiftsConfirm, handleGiftsClose, hasSelectedLogics]
        );

        /**
         * Handle removing a cart-level gift by gift_item_id
         * Uses removeCartLevelGiftFromCart mutation
         */
        const handleRemoveGift = useCallback(
            async (giftItemId: number) => {
                try {
                    const result = await removeCartLevelGiftFromCartMutation({
                        variables: {
                            input: {
                                cart_id: cartId ?? '',
                                gift_item_id: giftItemId,
                            },
                        },
                    });

                    if (result.data?.removeCartLevelGiftFromCart?.success) {
                        // Refetch available gifts after successful removal
                        refetchAvailableCartLevelGifts();
                    }
                } catch (error) {
                    console.error('Error removing cart-level gift:', error);
                }
            },
            [removeCartLevelGiftFromCartMutation, cartId, refetchAvailableCartLevelGifts]
        );

        const renderSelectedGift = useCallback(() => {
            return (
                <SelectedGiftCart
                    className={styles.giftSelectedOnly}
                    testId="selected-gift-cart"
                    onGiftPress={handleChangeGift}
                    onRemoveGift={handleRemoveGift}
                    showActionButton={showActionButton}
                    isMobile={isMobile}
                />
            );
        }, [handleChangeGift, handleRemoveGift, showActionButton, isMobile]);

        useEffect(() => {
            if (syncVersion && syncVersion > 0 && cartId) {
                refetchAvailableCartLevelGifts({ cartId });
            }
        }, [syncVersion, cartId, refetchAvailableCartLevelGifts]);

        if (!cartLevelGiftRules.length) {
            return null;
        }

        if (!giftsListLoading && !hasGiftsContent) {
            return null;
        }

        // If "show selected only" mode is enabled and there are no selected gifts,
        // skip rendering this component
        if (showSelectedOnly && !selectedGiftsCount) {
            return null;
        }

        return (
            <Fragment>
                <Fragment showIf={variant === 'detailed'}>
                    <SelectedGiftCart
                        testId="selected-gift-cart"
                        onGiftPress={handleChangeGift}
                        onRemoveGift={handleRemoveGift}
                        showActionButton={showActionButton}
                        isMobile={isMobile}
                    />
                    <CartGiftsSelectorWidget
                        cartLevelGiftRules={cartLevelGiftRules}
                        ref={cartGiftsSelectorRef}
                        onConfirm={onBeforeGiftsConfirm}
                        onClose={handleGiftsClose}
                    />
                </Fragment>
                {variant === 'compact' && (
                    <div className={styles.container}>
                        <SelectionGiftPrompt
                            icon={renderIcon}
                            text={title}
                            onPress={handleGiftPromptPress}
                            isMobile={isMobile}
                        />
                        <CartGiftsSelectorWidget
                            cartLevelGiftRules={cartLevelGiftRules}
                            ref={cartGiftsSelectorRef}
                            onConfirm={onBeforeGiftsConfirm}
                            onClose={handleGiftsClose}
                            renderJnjectedContent={renderSelectedGift}
                            showSelectedOnly={showSelectedOnly}
                        />
                    </div>
                )}
            </Fragment>
        );
    }
);

export default AvailableCartLevelGifts;
